import { takeEvery, put, call, fork, select } from 'redux-saga/effects';
import { message } from 'antd';
import { Client } from 'utils/client';
import { appLogout } from 'redux-root/actions';
import { PATH_PARTNER_USERS, ACTIVE, INACTIVE, DEFAULT_PARTNER_ID } from 'utils/appConstant';
import * as types from './actionTypes';
import { assertValidStatus, isValidStatus } from './statusHelper';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// PUT/GET single partner user: /api/v1/auth/partner/users/{partnerId}/{username}
const buildPartnerUserPath = (partnerId, username) =>
  `${PATH_PARTNER_USERS}/${encodeURIComponent(partnerId)}/${encodeURIComponent(username)}`;

// List response is a plain array; keep old wrapped formats working just in case
const toList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;
  return [];
};

// Create/update may return a plain object or the old { code, message, data } wrapper
const unwrap = (data) => (data && data.data !== undefined ? data.data : data);

const getErrorMessage = (error, fallback) =>
  error?.data?.message || error?.response?.data?.message || error?.message || fallback;

// Only send the fields the PUT API accepts; password only when a new one is entered
const buildUpdateBody = ({ email, fullName, status, password }) => {
  const body = { email, fullName, status: assertValidStatus(status) };
  if (password) body.password = password;
  return body;
};

const getUsers = (state) => state.partnerUserMngt?.users || [];

// Accept a table row, or an id that is looked up in the store
function* resolveUser(value) {
  let user = value;
  if (user !== null && user !== undefined && typeof user !== 'object') {
    const users = yield select(getUsers);
    user = users.find((u) => String(u.id) === String(value));
  }
  if (!user?.partnerId || !user?.username) {
    throw new Error('Missing partnerId or username for this partner user');
  }
  return user;
}

// Shared error handling: 401 logs the user out, anything else shows a message
function* handleError(error, failureType, fallback, reject) {
  console.log(error);
  if (error?.status === 401) {
    yield put(appLogout(true));
  } else {
    const errorMsg = getErrorMessage(error, fallback);
    message.error(errorMsg);
    yield put({ type: failureType, payload: errorMsg });
  }
  if (reject) reject(error);
}

// ---------------------------------------------------------------------------
// Workers
// ---------------------------------------------------------------------------

export function* partnerUserSearchCall() {
  try {
    const data = yield call(Client.getData, PATH_PARTNER_USERS, true);
    const users = toList(data);

    const invalid = users.filter((u) => !isValidStatus(u.status));
    if (invalid.length > 0) {
      console.warn(
        `[PartnerUsers] ${invalid.length} user(s) with invalid status:`,
        invalid.map((u) => ({ partnerId: u.partnerId, username: u.username, status: u.status }))
      );
    }

    yield put({
      type: types.FETCH_PARTNER_USERS_SUCCESS,
      payload: { data: users, rawData: users, totalElements: users.length, total: users.length },
    });
  } catch (error) {
    yield* handleError(error, types.FETCH_PARTNER_USERS_FAILURE, 'Failed to fetch partner users');
  }
}

export function* createPartnerUser(action) {
  const { payload, resolve, reject } = action;
  try {
    assertValidStatus(payload.status, 'Create');
    const { confirmPassword, ...rest } = payload;
    const body = { ...rest, partnerId: rest.partnerId || DEFAULT_PARTNER_ID };

    const response = yield call(Client.postData, PATH_PARTNER_USERS, body, true);
    yield put({ type: types.CREATE_PARTNER_USER_SUCCESS, payload: response });
    message.success('Partner user created successfully');

    if (resolve) resolve(response);
    yield put({ type: types.FETCH_PARTNER_USERS_REQUEST });
  } catch (error) {
    if (error?.status === 401 || error?.response?.status === 401) {
      yield put(appLogout(true));
    } else {
      const errorMsg = error?.status === 409
        ? (error?.data?.message || 'Partner user with this Partner ID and username already exists.')
        : (error?.data?.message || error?.message || 'Failed to create partner user');
      message.error(errorMsg);
      yield put({ type: types.CREATE_PARTNER_USER_FAILURE, payload: errorMsg });
      if (reject) reject(error);
    }
  }
}

export function* updatePartnerUser(action) {
  const { partnerId, username, payload = {}, resolve, reject } = action;
  try {
    if (!partnerId || !username) {
      throw new Error('Missing partnerId or username for this partner user');
    }

    const data = yield call(
      Client.putData,
      buildPartnerUserPath(partnerId, username),
      buildUpdateBody(payload),
      true,
      { isListObject: false }
    );
    const updated = unwrap(data);

    yield put({ type: types.UPDATE_PARTNER_USER_SUCCESS, payload: updated });
    message.success('Partner user updated successfully');
    if (resolve) resolve(updated);
    yield put({ type: types.FETCH_PARTNER_USERS_REQUEST });
  } catch (error) {
    yield* handleError(error, types.UPDATE_PARTNER_USER_FAILURE, 'Failed to update partner user', reject);
  }
}

// Activate and deactivate share the same PUT API, only the target status differs
function* changePartnerUserStatus(action, targetStatus, successType, failureType, label) {
  const { resolve, reject } = action;
  try {
    const user = yield* resolveUser(action.payload ?? action.user ?? action.id);

    if (!isValidStatus(user.status)) {
      throw new Error(`Cannot ${label} ${user.username}: invalid status "${user.status}"`);
    }
    if (user.status === targetStatus) {
      throw new Error(`Partner user ${user.username} is already ${targetStatus}`);
    }

    yield call(
      Client.putData,
      buildPartnerUserPath(user.partnerId, user.username),
      buildUpdateBody({ email: user.email, fullName: user.fullName, status: targetStatus }),
      true,
      { isListObject: false }
    );

    yield put({ type: successType, payload: user });
    message.success(`Partner user ${user.username} ${label}d successfully`);
    if (resolve) resolve();
    yield put({ type: types.FETCH_PARTNER_USERS_REQUEST });
  } catch (error) {
    yield* handleError(error, failureType, `Failed to ${label} partner user`, reject);
  }
}

export function* deactivatePartnerUser(action) {
  yield* changePartnerUserStatus(
    action,
    INACTIVE,
    types.DELETE_PARTNER_USER_SUCCESS,
    types.DELETE_PARTNER_USER_FAILURE,
    'deactivate'
  );
}

export function* activatePartnerUser(action) {
  yield* changePartnerUserStatus(
    action,
    ACTIVE,
    types.ACTIVATE_PARTNER_USER_SUCCESS,
    types.ACTIVATE_PARTNER_USER_FAILURE,
    'activate'
  );
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

function* watchPartnerUserSearch() {
  yield takeEvery(types.FETCH_PARTNER_USERS_REQUEST, partnerUserSearchCall);
}

function* watchCreatePartnerUser() {
  yield takeEvery(types.CREATE_PARTNER_USER_REQUEST, createPartnerUser);
}

function* watchUpdatePartnerUser() {
  yield takeEvery(types.UPDATE_PARTNER_USER_REQUEST, updatePartnerUser);
}

function* watchDeactivatePartnerUser() {
  yield takeEvery(types.DELETE_PARTNER_USER_REQUEST, deactivatePartnerUser);
}

function* watchActivatePartnerUser() {
  yield takeEvery(types.ACTIVATE_PARTNER_USER_REQUEST, activatePartnerUser);
}

export function* partnerUsersSaga() {
  yield fork(watchPartnerUserSearch);
  yield fork(watchCreatePartnerUser);
  yield fork(watchUpdatePartnerUser);
  yield fork(watchDeactivatePartnerUser);
  yield fork(watchActivatePartnerUser);
}

export default partnerUsersSaga;